import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DisplayDay, Payment, StatusConfig, YearDay, YearMonth } from 'src/app/shared/models/calendar.models';

@Component({
  selector: 'fun-calendar-pay-timetable',
  templateUrl: './calendar-pay-timetable.component.html',
  styles: [],
})
export class CalendarPayTimetableComponent implements OnInit {

  @Input() calendarBenefits: any;
  @ViewChild('detailContainer') detailContainer!: ElementRef;

  monthNames: string[] = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
  ];

  dayNames: string[] = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

  statusConfig: { [key: string]: StatusConfig } = {
    'Pagado': { label: this.translate.instant('CALENDAR.PAID'), color: "bg-cream-100 text-eggplant-80" },
    'Programado': { label: this.translate.instant('CALENDAR.PENDING'), color: "bg-active text-white" },
    'Aplazado': { label: this.translate.instant('CALENDAR.DELAY'), color: "bg-eggplant-70 text-white" },
    'Cancelado': { label: this.translate.instant('CALENDAR.CANCELED'), color: "bg-orange-100  text-white" },
  };

  payments: Payment[] = [];

  currentDate: Date = new Date();
  selectedDate: Date | null = null;
  selectedDatePayments: Payment[] = [];

  calendarDaysGrid: DisplayDay[] = [];

  viewMode: 'month' | 'year' = 'year';
  yearlyCalendar: YearMonth[] = [];

  private initialDate: Date = new Date();

  constructor(private translate: TranslateService) {
    const today = new Date();
    this.currentDate.setFullYear(today.getFullYear(), today.getMonth(), today.getDate()); // Junio es el mes 5
    this.currentDate.setDate(1); // Siempre empezar el mes en el día 1
    this.initialDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), 1);
  }

  ngOnInit() {
    for (let i = 0; i < this.calendarBenefits.length; i++) {
      const fullDate = `${this.calendarBenefits[i].date[2]}/${this.calendarBenefits[i].date[1]}/${this.calendarBenefits[i].date[0]}`;
      if (this.calendarBenefits[i].benefits && this.calendarBenefits[i].benefits.length > 0) {
        const benef = this.calendarBenefits[i].benefits;
        for (let j = 0; j < benef.length; j++) {
          if (benef[j].paymentStatus && fullDate) {
            const objModel = {
              id: i + 1,
              date: fullDate,
              title: benef[j].paymentTitle,
              project: benef[j].projectName,
              amount: benef[j].amount,
              status: benef[j].paymentStatus,
              description: benef[j].paymentDescription,
            }
            this.payments.push(objModel)
          }
        }
      }
    }
    //this.generateCalendarGrid();
    this.generateYearlyCalendar();
    const today = new Date();
    const todayForDemo = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    this.onDayClick(todayForDemo);
  }

  private parseDate(dateString: string): Date {
    const [day, month, year] = dateString.split("/").map(Number);
    const fullYear = year < 100 ? 2000 + year : year;
    return new Date(fullYear, month - 1, day);
  }

  private isSameDay(date1: Date, date2: Date): boolean {
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  }

  generateCalendarGrid(): void {
    this.calendarDaysGrid = [];
    const year = this.currentDate.getFullYear();
    const month = this.currentDate.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);

    // Calcular el día de la semana para el primer día del mes (0=Domingo, 1=Lunes, etc.)
    let startDayOfWeek = firstDayOfMonth.getDay(); // 0 (Domingo) - 6 (Sábado)

    // Ajustar para que el Lunes sea 0 y el Domingo sea 6
    // Si es Domingo (0), queremos que sea 6 (último día en nuestra nueva semana)
    // Si es Lunes (1), queremos que sea 0 (primer día)
    // Formula: (startDayOfWeek + 6) % 7
    let adjustedStartDayOffset = (startDayOfWeek + 6) % 7;


    // Rellenar días del mes anterior
    for (let i = 0; i < adjustedStartDayOffset; i++) {
      const prevMonthDay = new Date(year, month, 1 - (adjustedStartDayOffset - i));
      this.calendarDaysGrid.push({
        date: prevMonthDay,
        dayOfMonth: prevMonthDay.getDate(),
        dayOfWeek: prevMonthDay.getDay(),
        payments: [],
        isCurrentMonth: false,
      });
    }

    // Rellenar días del mes actual
    for (let day = 1; day <= lastDayOfMonth.getDate(); day++) {
      const cellDate = new Date(year, month, day);
      const dayPayments = this.payments.filter((payment) =>
        this.isSameDay(this.parseDate(payment.date), cellDate)
      );
      this.calendarDaysGrid.push({
        date: cellDate,
        dayOfMonth: day,
        dayOfWeek: cellDate.getDay(),
        payments: dayPayments,
        isCurrentMonth: true,
      });
    }

    // Rellenar días del mes siguiente
    const totalDaysInGrid = this.calendarDaysGrid.length;
    const remainingDays = 7 - (totalDaysInGrid % 7);
    if (remainingDays < 7 && remainingDays > 0) {
      for (let i = 1; i <= remainingDays; i++) {
        const nextMonthDay = new Date(year, month + 1, i);
        this.calendarDaysGrid.push({
          date: nextMonthDay,
          dayOfMonth: nextMonthDay.getDate(),
          dayOfWeek: nextMonthDay.getDay(),
          payments: [],
          isCurrentMonth: false,
        });
      }
    }
  }

  goToPreviousMonth(): void {
    if (this.currentDate.getFullYear() <= this.initialDate.getFullYear() &&
        this.currentDate.getMonth() <= this.initialDate.getMonth()) {
      return;
    }
    this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() - 1, 1);
    this.generateCalendarGrid();
  }

  goToNextMonth(): void {
    const maxDate = new Date(this.initialDate.getFullYear(), this.initialDate.getMonth() + 12, 1);
    if (this.currentDate.getFullYear() >= maxDate.getFullYear() &&
        this.currentDate.getMonth() >= maxDate.getMonth()) {
      return;
    }

    this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 1);
    this.generateCalendarGrid();
  }

  goToToday(): void {
    this.currentDate = new Date(this.initialDate.getFullYear(), this.initialDate.getMonth(), 1);
    this.generateCalendarGrid();
    const today = new Date();
    const todayForDemo = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    this.onDayClick(todayForDemo);
    this.scrollToDetailContainer();
  }

  onDayClick(dayDate?: Date): void {
    this.selectedDate = dayDate;
    this.selectedDatePayments = this.payments.filter((payment) =>
      this.isSameDay(this.parseDate(payment.date), dayDate)
    );
    if (this.selectedDatePayments.length > 0) {
      this.scrollToDetailContainer();
    }
  }

  scrollToDetailContainer(): void {
    this.detailContainer.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }

  getPaymentStatusClass(status: string): string {
    const config: { [key: string]: StatusConfig } = this.statusConfig;
    return config[status] ? config[status].color : '';
  }

  getMonthYearTitle(): string {
    return `${this.monthNames[this.currentDate.getMonth()]} ${this.currentDate.getFullYear()}`;
  }

  formatSelectedDayDate(): string {
    if (!this.selectedDate) {
      return '';
    }
    const options: Intl.DateTimeFormatOptions = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
    let formattedDate = this.selectedDate.toLocaleDateString('es-ES', options);

    const parts = formattedDate.split(' ');
    parts[0] = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
    for (let i = 0; i < parts.length; i++) {
        if (['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'].includes(parts[i].toLowerCase())) {
            parts[i] = parts[i].charAt(0).toUpperCase() + parts[i].slice(1);
        }
    }
    return parts.join(' ');
  }

  isTodayDay(date: Date): boolean {
    const todayForDemo = new Date(2025, 5, 17);
    return this.isSameDay(date, todayForDemo);
  }

  isSelectedDayInGrid(date: Date): boolean {
    return !!this.selectedDate && this.isSameDay(date, this.selectedDate);
  }

  isPreviousMonthDisabled(): boolean {
    return this.currentDate.getFullYear() <= this.initialDate.getFullYear() &&
           this.currentDate.getMonth() <= this.initialDate.getMonth();
  }

  isNextMonthDisabled(): boolean {
    const maxDate = new Date(this.initialDate.getFullYear(), this.initialDate.getMonth() + 12, 1);
    return this.currentDate.getFullYear() >= maxDate.getFullYear() &&
           this.currentDate.getMonth() >= maxDate.getMonth();
  }

  switchView(view: 'month' | 'year') {
    this.viewMode = view;
    if (view === 'year') {
      this.generateYearlyCalendar();
    } else {
      this.generateCalendarGrid();
    }
  }

  /** Construye los 12 meses siguientes empezando por currentDate */
  generateYearlyCalendar(): void {
    this.yearlyCalendar = [];

    const today = new Date();
    const year = today.getFullYear();
    const startMonth = today.getMonth();

    for (let i = 0; i < 12; i++) {
      const month = (startMonth + i) % 12;
      const yearOffset = Math.floor((startMonth + i) / 12);
      const actualYear = year + yearOffset;

      const firstDay = new Date(actualYear, month, 1);
      const daysInMonth = new Date(actualYear, month + 1, 0).getDate();
      const startDayOfWeek = (firstDay.getDay() + 6) % 7;

      const days = [];

      for (let j = 0; j < startDayOfWeek; j++) {
        days.push(null);
      }

      for (let d = 1; d <= daysInMonth; d++) {
        const dateObj = new Date(actualYear, month, d);

        const payments = this.payments.filter(p =>
          this.isSameDay(this.parseDate(p.date), dateObj)
        );

        const hasEvent = payments.length > 0;
        const highestStatus = hasEvent ? this.getHighestPriorityStatus(payments) : null;

        days.push({ day: d, hasEvent, status: highestStatus });
      }

      this.yearlyCalendar.push({
        month,
        year: actualYear,
        days,
      });
    }
  }

  private getHighestPriorityStatus(payments: Payment[]): string {
    const priority = ['Programado', 'Aplazado', 'Cancelado', 'Pagado'];
    for (const status of priority) {
      if (payments.find(p => p.status === status)) {
        return status;
      }
    }
    return payments[0]?.status ?? '';
  }

  getStatusColorClass(status: string): string {
    switch (status) {
      case 'Programado':
        return 'bg-active text-white rounded-full';
      case 'Aplazado':
        return 'bg-eggplant-70 text-white rounded-full';
      case 'Cancelado':
        return 'bg-orange-100 text-white rounded-full';
      case 'Pagado':
        return 'bg-cream-100 text-eggplant-80 rounded-full';
      default:
        return '';
    }
  }

  onYearDayClick(year: number, month: number, day: number): void {
    const selectedDate = new Date(year, month, day);
    this.onDayClick(selectedDate);
  }

}